import type { DocPage } from '../types'

export const glossary: DocPage = {
  id: 'glossary',
  title: 'Glossary',
  description: 'Key terms used throughout SEPM X-Translator.',
  category: 'Reference',
  sections: [
    { id: 'workspace', title: 'Workspace & models' },
    { id: 'mapping', title: 'Mapping & transfer' },
    { id: 'spatial', title: 'Spatial & styles' },
    { id: 'account', title: 'Account & status' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'workspace',
      text: 'Workspace & models',
    },
    {
      type: 'definitions',
      items: [
        {
          term: 'Source / Target',
          definition: 'Left and right data sides of a transfer.',
        },
        {
          term: 'Collection',
          definition: 'A feature class, layer, or table in the model tree.',
        },
        {
          term: 'Attribute',
          definition: 'A field on a collection.',
        },
        {
          term: 'Model tree',
          definition: 'Hierarchical schema browser for collections and attributes.',
        },
        {
          term: 'Configuration / Config',
          definition: 'Saved connection and filter settings for a format (Model Configuration).',
        },
        {
          term: 'Create Custom Config',
          definition:
            'Feature-gated action to save a new named Model Configuration (create_custom_config).',
        },
        {
          term: 'Default (config)',
          definition:
            'Configuration marked as default so New connections pre-fill its settings.',
        },
        {
          term: 'Feature Options',
          definition:
            'Show Feature Counts, Hide Feature with 0 records, Show Collection Details.',
        },
        {
          term: 'Filters modal',
          definition:
            'Collections / Attributes tabs that limit what loads into the model and transfer.',
        },
        {
          term: 'Logical object',
          definition:
            'User-defined collection, attribute, or enumerator extending the schema.',
        },
        {
          term: 'Enumerator',
          definition:
            'PostGIS-only logical PostgreSQL ENUM type with named values for ENUM attributes.',
        },
        {
          term: 'Select Format',
          definition: 'Source/Target control to pick a format and open New or saved configs.',
        },
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'mapping',
      text: 'Mapping & transfer',
    },
    {
      type: 'definitions',
      items: [
        {
          term: 'Transfer Profile',
          definition: 'Saved package of source config, target config, and mapping.',
        },
        {
          term: 'Mapped / Scratch',
          definition: 'Transfer types: explicit mapping vs create-from-source schema (MAPPED / SCRATCH).',
        },
        {
          term: 'Connect',
          definition: 'Link collection or attribute pairs for a mapped transfer.',
        },
        {
          term: 'Disconnect',
          definition: 'Remove a selected mapping link from the Mapping panel.',
        },
        {
          term: 'Connect-ID',
          definition: 'Match keys used to update existing target rows.',
        },
        {
          term: 'Connect-Delete',
          definition: 'Match keys used to delete target rows.',
        },
        {
          term: 'Predicate expression',
          definition:
            'Boolean expression over key indices (for example 1 OR 2) combining Connect-ID/Delete keys.',
        },
        {
          term: 'Create-on-no-match',
          definition:
            'Global/per-collection policy to insert when Connect-ID finds no target row.',
        },
        {
          term: 'Fixed mapping',
          definition: 'Constant value assignment to an attribute (Add fixed mapping / Set fixed value).',
        },
        {
          term: 'Value mapping',
          definition: 'Conditional value transform rules with operators and Else (no match).',
        },
        {
          term: 'Value mapping reference',
          definition: 'Shared named rule set assigned to an attribute mapping.',
        },
        {
          term: 'Else modes',
          definition: 'Use source value (use_source), Default value (default_value), Set as null (null).',
        },
        {
          term: 'Auto Map',
          definition:
            'Suggested mappings window with Strict / Balanced / Exploratory strategies and trust levels.',
        },
        {
          term: 'Transfer Review',
          definition: 'Pre-run summary of formats, models, mapping type, coordinates, and filters.',
        },
        {
          term: 'Inactive mapping',
          definition:
            'Mapping that no longer resolves against the loaded models; remove with Remove inactive mapping.',
        },
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'spatial',
      text: 'Spatial & styles',
    },
    {
      type: 'definitions',
      items: [
        {
          term: 'Model Mode (CAD)',
          definition: 'Translator, Layer, or Entity organization of CAD entities.',
        },
        {
          term: 'EPSG / Override EPSG',
          definition: 'Coordinate reference system code; CAD source override via EPSG Code.',
        },
        {
          term: 'Coordinate Conversion',
          definition: 'Global XY offset (xy-coordinate) applied in transfer settings.',
        },
        {
          term: 'Inclusion mode',
          definition: 'Clip, Intersect, or Within for spatial filters.',
        },
        {
          term: 'Attribute Table',
          definition: 'Tabular inspector for map layer features with search, filters, pagination.',
        },
        {
          term: 'Style fidelity',
          definition: 'Basic / Standard / High Fidelity quality for CAD style import.',
        },
        {
          term: 'layer_styles',
          definition: 'QGIS styles table written into GeoPackage or PostGIS.',
        },
        {
          term: 'QML / SLD / XStyles',
          definition: 'Style export sidecar formats (QGIS, OGC SLD, SEPM JSON).',
        },
        {
          term: 'MGDM Pipeline',
          definition: 'UI label for the Interlis demo target format (interlis_demo).',
        },
        {
          term: 'ACE',
          definition: 'Smallworld Alternative Code Environment selection for class visibility/styles.',
        },
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'account',
      text: 'Account & status',
    },
    {
      type: 'definitions',
      items: [
        {
          term: 'UNSAVED',
          definition:
            'Indicator that the current configuration has not been saved (also Transfer Review tip).',
        },
        {
          term: 'Unsaved Changes',
          definition: 'Chrome state showing the live model differs from the last saved config/profile.',
        },
        {
          term: 'LIVE subscription',
          definition: 'Only subscription state that unlocks X-Translator formats and features.',
        },
        {
          term: 'Access restricted',
          definition: 'Dialog shown when a feature entitlement is missing.',
        },
        {
          term: 'Refresh Account',
          definition: 'Profile menu action to re-fetch entitlements from SEPM.',
        },
      ],
    },
  ],
}
